import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { useAppStateContext } from 'src/AppState/AppState';
import Icon from 'src/components/Icon/Icon';
import { LiabilitiesState } from 'src/domains/sportsbook/trading/view/Liabilities/Liabilities.state';
import { makeAutoObservable, observable } from 'mobx';
import { EventMarketItemType } from 'src_common/common/websocket2/modelsApi/EventMarkets';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { MarketId } from 'src_common/common/websocket2/id/WebsocketId';
import { EventPageState } from 'src/domains/sportsbook/trading/state/EventPageState';

import { RouterBackoffice } from 'src/AppState/RouterBackoffice';

interface StateType {
    eventMarkets: Map<string, Array<EventMarketItemType>>;
}

interface LiabilitiesListRowType {
    eventId: number;
    liabilitiesState: LiabilitiesState;
}

const getOrCreate = (
    eventMarkets: Map<string, Array<EventMarketItemType>>,
    templateId: string
): Array<EventMarketItemType> => {
    const list = eventMarkets.get(templateId);

    if (list !== undefined) {
        return list;
    }

    const newList: Array<EventMarketItemType> = [];
    eventMarkets.set(templateId, newList);
    return newList;
};

const getState = (markets: Array<EventMarketItemType>): StateType => {
    const eventMarkets: Map<string, Array<EventMarketItemType>> = new Map();

    for (const market of markets) {
        const templateId = market.templateId;
        const list = getOrCreate(eventMarkets, templateId);
        list.push(market);
    }

    return {
        eventMarkets,
    };
};

class MarketState {
    @observable public isOpen: boolean = false;

    public toogle = (): void => {
        this.isOpen = !this.isOpen;
    };

    public constructor() {
        makeAutoObservable(this);
    }
}
class LiabilitiesListRowState {
    @observable public opened: boolean = false;

    @observable.ref public marketsState: MobxMapAutoNew<string, MarketState> = new MobxMapAutoNew(
        () => new MarketState()
    );

    public constructor(
        private readonly eventPageState: EventPageState,
        private readonly routerBackoffice: RouterBackoffice
    ) {
        makeAutoObservable(this);
    }

    public toggle(): void {
        this.opened = !this.opened;

        if (this.opened) {
            this.marketsState = new MobxMapAutoNew(() => new MarketState());
        }
    }

    public onOpenEvent(eventId: number, marketId: MarketId): void {
        this.eventPageState.addEvent(eventId);
        this.eventPageState.addToRecentlyViewed(eventId);
        this.routerBackoffice.redirectToNamespaceEventMarket(eventId, marketId);
    }

    public toggleMarketGroup(marketTemplateId: string): void {
        this.marketsState.get(marketTemplateId).toogle();
    }
}

export const LiabilitiesListRow = observer(({ eventId }: LiabilitiesListRowType) => {
    const {
        websocketV2,
        sportsbookState: { eventPageState },
        routerBackoffice,
        reduxAction,
    } = useAppStateContext();
    const eventModel = websocketV2.getEvent(eventId);
    const markets = websocketV2.getEventMarkets(eventId) ?? [];
    const { eventMarkets } = getState(markets);
    const [state] = useState(() => new LiabilitiesListRowState(eventPageState, routerBackoffice));

    const opened = state.opened;

    const renderRowHeader = (markets: Array<EventMarketItemType>, templateId: string): Array<React.ReactElement> => {
        const opened = state.opened;
        const marketsState = state.marketsState;
        const market = markets[0];

        if (market === undefined) {
            return [];
        }

        const marketId = market.id2;

        const clsName = classNames({ 'liabilities-markets-table-row__opened': opened });

        return [
            <tr
                key={templateId}
                className={clsName}
            >
                <td data-test-market-id={marketId.rawId}>
                    <a
                        href='#'
                        className={classNames({
                            'icon-wrap': markets.length > 1,
                            'is--open': opened && marketsState.get(market.templateId).isOpen,
                        })}
                        onClick={(e): void => {
                            e.preventDefault();

                            if (markets.length <= 1) {
                                state.onOpenEvent(eventId, marketId);
                            } else {
                                state.toggleMarketGroup(market.templateId);
                            }
                        }}
                    >
                        {markets.length > 1 ? <Icon name='down' /> : undefined}
                        {market.name}
                    </a>
                    {markets.length === 1 ? (
                        <div className='event-markets-liabilities-groups'>
                            <a
                                href='#'
                                className='event-markets-liabilities-badge green'
                                onClick={reduxAction.onGetLiabilitiesRequest.bind(this, marketId, 'single')}
                                data-test='liabilities-singles'
                            >
                                Single
                            </a>

                            <a
                                href='#'
                                className='event-markets-liabilities-badge green'
                                onClick={reduxAction.onGetLiabilitiesRequest.bind(this, marketId, 'multiple')}
                                data-test='liabilities-multiple'
                            >
                                Multiple
                            </a>

                            <a
                                href='#'
                                className='event-markets-liabilities-badge green'
                                onClick={reduxAction.onGetLiabilitiesRequest.bind(this, marketId, 'both')}
                                data-test='liabilities-both'
                            >
                                Both
                            </a>
                        </div>
                    ) : undefined}
                </td>
            </tr>,
        ];
    };

    const renderRow = (markets: Array<EventMarketItemType>, templateId: string): Array<React.ReactElement> => {
        const opened = state.opened;
        const marketsState = state.marketsState;

        const rowArray = renderRowHeader(markets, templateId);

        if (markets.length < 1) {
            return rowArray;
        }

        return rowArray.concat(
            markets.map((m) => (
                <tr
                    key={`market-${m.id}`}
                    className={classNames('event-markets-table-inner', {
                        'liabilities-markets-table-row__opened': opened && marketsState.get(m.templateId).isOpen,
                    })}
                >
                    <td data-test-inner-market-id={m.id}>
                        <a
                            href='#'
                            onClick={(e): void => {
                                e.preventDefault();
                                state.onOpenEvent(eventId, m.id2);
                            }}
                        >
                            {m.name}
                        </a>
                        {
                            <div className='event-markets-liabilities-groups'>
                                <a
                                    href='#'
                                    className='event-markets-liabilities-badge green'
                                    onClick={reduxAction.onGetLiabilitiesRequest.bind(this, m.id2, 'single')}
                                >
                                    Single
                                </a>

                                <a
                                    href='#'
                                    className='event-markets-liabilities-badge green'
                                    onClick={reduxAction.onGetLiabilitiesRequest.bind(this, m.id2, 'multiple')}
                                >
                                    Multiple
                                </a>

                                <a
                                    href='#'
                                    className='event-markets-liabilities-badge green'
                                    onClick={reduxAction.onGetLiabilitiesRequest.bind(this, m.id2, 'both')}
                                >
                                    Both
                                </a>
                            </div>
                        }
                    </td>
                </tr>
            ))
        );
    };

    const marketsElOut = [];

    for (const [templateId, markets] of eventMarkets.entries()) {
        marketsElOut.push(renderRow(markets, templateId));
    }

    const marketsEl =
        marketsElOut.length > 0 ? (
            <table className='table liabilities-markets-table form'>
                <tbody>{marketsElOut}</tbody>
            </table>
        ) : null;

    return (
        <tr data-test-event-id={eventId}>
            <td className='trading-events-list-table-name'>
                <strong
                    title={`${eventMarkets.size} markets`}
                    className={classNames('icon-wrap', { 'is--open': opened })}
                    onClick={(e): void => {
                        e.preventDefault();
                        e.stopPropagation();
                        state.toggle();
                    }}
                >
                    {eventMarkets.size > 0 ? <Icon name='down' /> : undefined}
                    {eventModel?.name}
                </strong>
                {marketsEl}
            </td>
        </tr>
    );
});
